import React, { useCallback, useMemo } from 'react';
import differenceInDays from 'date-fns/differenceInDays';
import { useTranslation } from 'react-i18next';

import { JobApplicationStatusKey } from '@restworld/utility-types';
import { IBadgeProps, type ButtonVariantType } from '@restworld/ui-ds-base';

import {
  EmployerCandidateCard,
  EmployerCandidateCardHeader,
  EmployerCandidateCardInfo,
  EmployerCandidatesCardLabels,
  EmployerCandidateRowCard,
  EmployerCandidateRowInfo,
  EmployerCandidateRowDistance,
  EmployerCandidateRowLabels,
  EmployerCandidateUnsuitabilityReason,
  // EmployerCandidateRowContact,
} from './employer-candidate-card';
import { useResponsiveBreakPoints, cn } from '@restworld/utils-common';

export function EmployerCandidateKanbanCard({
  candidate,
  ctaButton,
  distance,
  labels,
  unsuitabilityReason,
  lastStatusChangeDate,
  wrapperStyle,
  statusKey,
  section,
  footerSlot,
  forceColumnLayout,
}: EmployerCandidateKanbanCardProps) {
  const { getCandidateLastChangeBadgeInfo } = useCandidateLastChangeBadgeInfo();
  const { isDesktop } = useResponsiveBreakPoints();

  const { badgeVariant, badgeText } = useMemo(
    () => getCandidateLastChangeBadgeInfo(lastStatusChangeDate, statusKey),
    [getCandidateLastChangeBadgeInfo, lastStatusChangeDate, statusKey]
  );

  const badge = useMemo(() => ({ variant: badgeVariant, text: badgeText }), [badgeText, badgeVariant]);
  const currrentCta = useMemo(
    () => ({ label: ctaButton.label, onClick: ctaButton.onClick, variant: ctaButton.variant }),
    [ctaButton.label, ctaButton.onClick, ctaButton.variant]
  );

  // forceColumnLayout skips the desktop row variant: the JP-page card stack
  // renders the compact column card on every breakpoint while keeping the
  // 'shortlisted' semantics (always-visible CTA footer).
  if (isDesktop && section === 'shortlisted' && !forceColumnLayout) {
    return (
      <EmployerCandidateRowCard
        ctaButton={currrentCta}
        className={wrapperStyle}
        footerSlot={footerSlot && <RowFooterSlot>{footerSlot}</RowFooterSlot>}
      >
        <EmployerCandidateRowInfo
          firstName={candidate.firstName}
          lastName={candidate.lastName}
          badge={badge}
          showFire={candidate?.isRecommended}
          candidateImage={candidate?.image}
        />
        <EmployerCandidateRowDistance distance={distance} />
        {unsuitabilityReason ? (
          <EmployerCandidateUnsuitabilityReason reason={unsuitabilityReason} row />
        ) : (
          <EmployerCandidateRowLabels labels={labels?.values} />
        )}
        {/* <EmployerCandidateRowContact phoneNumber={candidate?.phoneNumber} whatsappNumber={candidate?.phoneNumber} /> */}
      </EmployerCandidateRowCard>
    );
  }

  return (
    <EmployerCandidateCard
      ctaButton={currrentCta}
      className={wrapperStyle}
      newShortlistedSection={section === 'shortlisted'}
    >
      <EmployerCandidateCardHeader firstName={candidate.firstName} lastName={candidate.lastName} />
      <EmployerCandidateCardInfo
        candidateImage={candidate.image}
        firstName={candidate.firstName}
        lastName={candidate.lastName}
        badge={badge}
        distance={distance}
        showFire={candidate?.isRecommended}
      />
      {unsuitabilityReason ? (
        <EmployerCandidateUnsuitabilityReason reason={unsuitabilityReason} />
      ) : (
        <EmployerCandidatesCardLabels labels={labels.values} variant="neutral" />
      )}
      {footerSlot && <ColumnFooterSlot>{footerSlot}</ColumnFooterSlot>}
    </EmployerCandidateCard>
  );
}
export default EmployerCandidateKanbanCard;

// Span the full row of the desktop grid (`grid-cols-9`) so the footer renders
// as a new row beneath name/distance/labels instead of squeezing into a cell.
const RowFooterSlot = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('col-span-9 mt-3 pt-3 border-t border-neutral-40', className)} {...props} />
);

// Column variant (mobile): container is already flex-col with gap, so the
// footer naturally sits on its own row beneath the labels — no divider needed.
const ColumnFooterSlot = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('', className)} {...props} />
);

export interface EmployerCandidateKanbanCardProps {
  ctaButton: { label: string; onClick?: () => void; variant?: ButtonVariantType };
  candidate: {
    image?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    isRecommended?: boolean;
  };
  distance: string;
  labels: { values: string[] };
  /**
   * Concise sentence explaining why the candidate is not suitable. When set,
   * it replaces the strength labels (used on the "Non idonei" tab, where the
   * highlights are hidden because they would be misleading).
   */
  unsuitabilityReason?: string;
  lastStatusChangeDate: string;
  wrapperStyle?: string;
  statusKey: JobApplicationStatusKey;
  section?: 'shortlisted';
  /**
   * Render the column (mobile) variant on every breakpoint, keeping the
   * `section` semantics (e.g. the always-visible CTA of 'shortlisted').
   * Used by the JP-page candidate card stack.
   */
  forceColumnLayout?: boolean;
  /**
   * Optional content rendered as the last row inside the card (after labels).
   * Used by callers to attach contextual chips/badges (e.g. "JP updated after
   * this candidate entered selection") that must visually belong to the card.
   */
  footerSlot?: React.ReactNode;
}

export function useCandidateLastChangeBadgeInfo() {
  const { t: translateCommon } = useTranslation('common');

  const getCandidateLastChangeBadgeInfo = useCallback(
    (lastStatusChangeDate: string, statusKey: JobApplicationStatusKey) => {
      const daysSinceChange = differenceInDays(new Date(), new Date(lastStatusChangeDate));
      let badgeVariant: IBadgeProps['variant'];
      let badgeText: string;
      if (statusKey === 'worker_jp_in_shortlist') {
        badgeVariant = 'fresh';
        badgeText = translateCommon('new');
      } else if (daysSinceChange === 0) {
        badgeVariant = 'success';
        badgeText = translateCommon('today');
      } else {
        badgeVariant = daysSinceChange <= 3 ? 'warning' : 'danger';
        badgeText =
          daysSinceChange > 1
            ? `${daysSinceChange} ${translateCommon('days_ago')}`
            : `${daysSinceChange} ${translateCommon('day_ago')}`;
      }
      return { badgeVariant, badgeText };
    },
    [translateCommon]
  );

  return { getCandidateLastChangeBadgeInfo };
}
